import { gql } from '@/lib/api'
import { MENU_QUERY } from '@/lib/queries'
import type { DeliveryLocation, KioskConfig, MenuPage, MenuType, PaymentMethod, TimeSlot } from '@/lib/types'
import { isAvailable } from '@/lib/utils'

export interface MenuData {
  restaurant: {
    name: string | null
    menu: { pages: MenuPage[] }
    paymentMethods: PaymentMethod[]
    deliveryLocations: DeliveryLocation[]
    deliveryTimeSlots: TimeSlot[]
    showsTipInClient: boolean | null
    queueTime: string | null
  }
}

export const MENU_CACHE_TTL = 15_000
const cache = new Map<string, { promise: Promise<MenuData>; expiresAt: number }>()

export function clearMenuCache() {
  cache.clear()
}

export function fetchMenu(config: KioskConfig, menuType: MenuType, token: string): Promise<MenuData> {
  const key = JSON.stringify([config.apiEndpoint, config.restaurantId, config.tillId, menuType, token])
  const cached = cache.get(key)
  if (cached && cached.expiresAt > Date.now()) return cached.promise
  // Infinity deduplicates the in-flight request; freshness begins at completion.
  const request: Promise<MenuData> = gql(config.apiEndpoint, MENU_QUERY, { id: config.restaurantId, menuType }, token)
  const entry: { promise: Promise<MenuData>; expiresAt: number } = {
    expiresAt: Infinity,
    promise: request
      .then((data) => {
        if (!Array.isArray(data?.restaurant?.menu?.pages)) throw new Error('Kunde inte ladda menyn')
        entry.expiresAt = Date.now() + MENU_CACHE_TTL
        warmCategoryImages(data.restaurant.menu.pages)
        return data
      })
      .catch((error: unknown) => {
        if (cache.get(key) === entry) cache.delete(key)
        throw error
      }),
  }
  cache.set(key, entry)
  // Retain at most both menu types for a handful of contexts, only in memory.
  while (cache.size > 4) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    cache.delete(oldest)
  }
  return entry.promise
}

export async function loadMenu(config: KioskConfig, menuType: MenuType, token: string): Promise<MenuData> {
  try {
    return await fetchMenu(config, menuType, token)
  } catch {
    return fetchMenu(config, menuType, token)
  }
}

function warmCategoryImages(pages: MenuPage[]) {
  if (typeof Image === 'undefined') return
  const urls = new Set(
    pages.slice(0, 6).map((page) => page.categories.flatMap((category) => category.products).find((product) => isAvailable(product) && product.properties?.imageUrl)?.properties?.imageUrl),
  )
  for (const url of urls) {
    if (!url) continue
    const image = new Image()
    image.decoding = 'async'
    image.src = url
  }
}
